import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import React, { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { swapChart } from "../../../axios/Eventlog/EventLogApi";
import { DateTime } from "luxon";
import { useLoading } from "../../../context/LoadingContext";

dayjs.extend(utc);

const centerTextPlugin = {
  id: "requestCenterText",
  beforeDraw(chart) {
    const { width, height, ctx } = chart;

    const total =
      chart?.config?.options?.plugins?.requestCenterText?.total ?? 0;

    const title =
      chart?.config?.options?.plugins?.requestCenterText?.title ??
      "Total Request";

    ctx.restore();

    const titleFont = Math.round(width / 18);
    const valueFont = Math.round(width / 8);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const maxWidth = width * 0.35;
    const words = title.split(" ");

    const lines = [];
    let line = "";

    ctx.font = `${titleFont}px sans-serif`;

    words.forEach((word) => {
      const testLine = line ? `${line} ${word}` : word;

      if (ctx.measureText(testLine).width > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = testLine;
      }
    });

    if (line) {
      lines.push(line);
    }

    const lineHeight = titleFont + 2;

    const startY =
      height / 2 -
      (lines.length * lineHeight) / 2 -
      valueFont / 2;

    ctx.fillStyle = "#1F2937";

    lines.forEach((text, index) => {
      ctx.fillText(
        text,
        width / 2,
        startY + index * lineHeight
      );
    });

    ctx.font = `600 ${valueFont}px sans-serif`;
    ctx.fillStyle = "#1F2937";

    ctx.fillText(
      String(total),
      width / 2,
      startY + lines.length * lineHeight + valueFont
    );

    ctx.save();
  },
};

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
);

const RequestChart = ({
  selectedUser,
  selectedGroup,
  selectedShift,
  selectedDate,
  selectedStatus,
  refreshData,
}) => {
  const { t } = useTranslation();
  const [show, hide] = useLoading();

  const { genaralSettingTimeZone, isEmployee, profile } =
    useAuth();

  const [chartDataApi, setChartDataApi] = useState({
    approved: 0,
    rejected: 0,
    pending: 0,
  });

  const getMonthRangeByTZ = (date, timeZone) => {
    const base = DateTime.fromJSDate(date, {
      zone: timeZone || "UTC",
    });

    return {
      from: base.startOf("day").toUTC().toISO(),
      to: base.endOf("day").toUTC().toISO(),
    };
  };

  useEffect(() => {
    const fetchChart = async () => {
      try {
        show();

        const { from, to } = getMonthRangeByTZ(
          selectedDate,
          genaralSettingTimeZone
        );

        const payload = {
          accountId: isEmployee
            ? profile?.accountId
            : null,
          userId: selectedUser?.id || null,
          groupId: selectedGroup || null,
          shiftId: selectedShift || null,
          requestStatusId: selectedStatus || null,
          from,
          to,
        };

        const res = await swapChart(payload);

        setChartDataApi(
          res?.data?.data?.[0] || {
            approved: 0,
            rejected: 0,
            pending: 0,
          }
        );
      } catch (err) {
        console.error(err);
      } finally {
        hide();
      }
    };

    if (selectedDate) {
      fetchChart();
    }
  }, [
    selectedUser?.id,
    selectedGroup,
    selectedShift,
    selectedDate,
    selectedStatus,
    refreshData,
  ]);

  const approved = chartDataApi?.approved || 0;
  const rejected = chartDataApi?.rejected || 0;
  const requested = chartDataApi?.pending || 0;

  const total =
    chartDataApi?.total ||
    approved + rejected + requested;

  const activeSegments = [
    approved,
    rejected,
    requested,
  ].filter(item => item > 0).length;

  const hasMultipleSegments = activeSegments > 1;

  const chartData = useMemo(
    () => ({
      labels: [
        t("common.approved"),
        t("common.rejected"),
        t("common.requested"),
      ],
      datasets: [
        {
          data: [approved, rejected, requested],
          backgroundColor: [
            "#058900",
            "#D00D00",
            "#F97C00",
          ],
          borderWidth: 0,
          spacing: hasMultipleSegments ? 2 : 0,
          borderRadius: hasMultipleSegments ? 8 : 0,
        },
      ],
    }),
    [approved, rejected, requested, t]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      plugins: {
        legend: {
          display: false,
        },
        requestCenterText: {
          title: t("common.total-request"),
          total,
        },
      },
    }),
    [t, total]
  );

  const legendData = [
    {
      color: (
        <div className="rounded-full w-3 h-3 bg-[#058900]" />
      ),
      display: t("common.approved"),
      value: approved,
    },
    {
      color: (
        <div className="rounded-full w-3 h-3 bg-[#D00D00]" />
      ),
      display: t("common.rejected"),
      value: rejected,
    },
    {
      color: (
        <div className="rounded-full w-3 h-3 bg-[#F97C00]" />
      ),
      display: t("common.requested"),
      value: requested,
    },
  ];

  return (
    <div className="px-3 sm:px-6 py-4 h-full flex flex-col justify-between w-full min-w-0 gap-3">
      <div className="flex items-center justify-center flex-1 w-full">
        <div className="w-full max-w-[220px] sm:max-w-[260px] md:max-w-[280px] aspect-square">
          {total > 0 ? (
            <Doughnut
              data={chartData}
              options={options}
              plugins={[centerTextPlugin]}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-center text-gray-400 px-5">
                {t("common.content-197")}
              </p>
            </div>
          )}
        </div>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-8 gap-y-2 sm:gap-y-3 px-1 sm:px-2 w-full">
          {legendData.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-center gap-1.5 py-1"
            >
              {item.color}
              <p className="text-[11px] sm:text-xs text-[#262626] whitespace-nowrap">
                {item.display}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequestChart;